import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useJobs } from "@/lib/api/hooks";
import { formatTokens } from "@/lib/format";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export const Route = createFileRoute("/_authenticated/usage")({
  component: UsagePage,
  head: () => ({ meta: [{ title: "Usage — CSaaS" }] }),
});

const COST_PER_1K = 0.003;

function UsagePage() {
  const { data: jobs = [] } = useJobs();

  const series = useMemo(() => {
    const days: { day: string; tokens: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      const dayLabel = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      const tokens = jobs
        .filter((j) => {
          const t = new Date(j.createdAt);
          t.setHours(0, 0, 0, 0);
          return t.getTime() === d.getTime();
        })
        .reduce((s, j) => s + (j.tokensUsed ?? 0), 0);
      days.push({ day: dayLabel, tokens });
    }
    return days;
  }, [jobs]);

  const bySkill = useMemo(() => {
    const map = new Map<string, { runs: number; tokens: number }>();
    for (const j of jobs) {
      const cur = map.get(j.skillName) ?? { runs: 0, tokens: 0 };
      cur.runs += 1;
      cur.tokens += j.tokensUsed ?? 0;
      map.set(j.skillName, cur);
    }
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [jobs]);

  const totalTokens = bySkill.reduce((s, r) => s + r.tokens, 0);
  const estCost = (totalTokens / 1000) * COST_PER_1K;

  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Usage</h1>
        <p className="text-sm text-muted-foreground mt-1">Token consumption and cost estimate.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-xs text-muted-foreground">Total tokens</div>
            <div className="font-display text-3xl font-semibold mt-1">
              {formatTokens(totalTokens)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-xs text-muted-foreground">Total runs</div>
            <div className="font-display text-3xl font-semibold mt-1">{jobs.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-xs text-muted-foreground">Estimated cost</div>
            <div className="font-display text-3xl font-semibold mt-1">${estCost.toFixed(2)}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tokens · last 14 days</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="day"
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="var(--color-muted-foreground)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--color-popover)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="tokens" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top skills</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs">
              <tr className="text-left">
                <th className="px-4 py-2.5 font-medium">Skill</th>
                <th className="px-4 py-2.5 font-medium text-right">Runs</th>
                <th className="px-4 py-2.5 font-medium text-right">Tokens</th>
                <th className="px-4 py-2.5 font-medium text-right">Est. cost</th>
              </tr>
            </thead>
            <tbody>
              {bySkill.map((r) => (
                <tr key={r.name} className="border-t border-border">
                  <td className="px-4 py-3">
                    <code className="font-mono text-xs">{r.name}</code>
                  </td>
                  <td className="px-4 py-3 text-right">{r.runs}</td>
                  <td className="px-4 py-3 text-right">{formatTokens(r.tokens)}</td>
                  <td className="px-4 py-3 text-right">
                    ${((r.tokens / 1000) * COST_PER_1K).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
